import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Table } from 'react-bootstrap';
import { getDatabase, ref, onValue, push, update, remove } from 'firebase/database';
import { FaEdit, FaTrash, FaBook, FaClock, FaRupeeSign } from 'react-icons/fa';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    duration: '',
    fees: '',
    description: '',
    instructor: '',
    prerequisites: '',
    maxStudents: '',
    status: 'Active'
  });

  useEffect(() => {
    const db = getDatabase();
    const coursesRef = ref(db, 'courses');
    const unsubscribe = onValue(coursesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const coursesList = Object.entries(data).map(([id, course]) => ({
          id,
          ...course
        }));
        setCourses(coursesList);
      } else {
        setCourses([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const db = getDatabase();
    
    if (editingCourse) {
      await update(ref(db, `courses/${editingCourse.id}`), formData);
    } else {
      await push(ref(db, 'courses'), formData);
    }
    
    handleClose();
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData(course);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      const db = getDatabase();
      await remove(ref(db, `courses/${id}`));
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingCourse(null);
    setFormData({
      name: '',
      code: '',
      duration: '',
      fees: '',
      description: '',
      instructor: '',
      prerequisites: '',
      maxStudents: '',
      status: 'Active'
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontWeight: 700, color: '#1976d2' }}>Courses Management</h2>
        <Button 
          variant="primary" 
          onClick={() => setShowModal(true)}
          style={{ borderRadius: '8px', padding: '8px 16px' }}
        >
          Add New Course
        </Button>
      </div>

      <Row className="g-4">
        {courses.map((course) => (
          <Col key={course.id} lg={4} md={6}>
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <h5 style={{ fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>{course.name}</h5>
                    <small style={{ color: '#666' }}>Code: {course.code}</small>
                  </div>
                  <div>
                    <Button 
                      variant="link" 
                      onClick={() => handleEdit(course)}
                      style={{ padding: '4px' }}
                    >
                      <FaEdit color="#1976d2" />
                    </Button>
                    <Button 
                      variant="link" 
                      onClick={() => handleDelete(course.id)}
                      style={{ padding: '4px' }}
                    >
                      <FaTrash color="#d32f2f" />
                    </Button>
                  </div>
                </div>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaClock />
                          <strong>Duration:</strong>
                        </div>
                      </td>
                      <td>{course.duration}</td>
                    </tr>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaRupeeSign />
                          <strong>Fees:</strong>
                        </div>
                      </td>
                      <td>₹{course.fees}</td>
                    </tr>
                    <tr>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaBook />
                          <strong>Instructor:</strong>
                        </div>
                      </td>
                      <td>{course.instructor}</td>
                    </tr>
                    <tr>
                      <td><strong>Max Students:</strong></td>
                      <td>{course.maxStudents}</td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: course.status === 'Active' ? '#4caf50' : '#f44336',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {course.status}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </Table>
                {course.description && (
                  <div style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
                    <strong>Description:</strong><br />
                    {course.description}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingCourse ? 'Edit Course' : 'Add New Course'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Course Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Course Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="e.g. 6 months"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Fees (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.fees}
                    onChange={(e) => setFormData({ ...formData, fees: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Instructor</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.instructor}
                    onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Max Students</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.maxStudents}
                    onChange={(e) => setFormData({ ...formData, maxStudents: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Prerequisites</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.prerequisites}
                    onChange={(e) => setFormData({ ...formData, prerequisites: e.target.value })}
                    placeholder="e.g. Basic knowledge of mathematics"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCourse ? 'Update' : 'Add'} Course
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Courses; 